import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import BarcodeScanner from './BarcodeScanner';
import Toast from './Toast';
import { submitStockTake, getStockTakeHistory, getICDocument, saveScanProgress, getSession, abandonSession } from '../supabase/services';
import useBarcodeBuffer from '../hooks/useBarcodeBuffer';
import useToast from '../hooks/useToast';
import { useModalOpenCount } from '../hooks/useAnyModalOpen';
import scanFeedback from '../utils/scanFeedback';
import { MEDIA } from '../utils/breakpoints';
import { barcodesMatch } from '../utils/barcode';
import { ScanLine, History, Trash2, Circle, ArrowRight, FileText, Send, RotateCcw, CloudOff, Cloud, Loader, X, Info } from 'lucide-react';
import './StockTake.css';
import QuantityPopup from './stocktake/QuantityPopup';
import LineEditDialog from './stocktake/LineEditDialog';
import { ReviewSubmitDialog, SuccessModal, ErrorModal, DangerConfirmDialog, ResumeSessionDialog } from './stocktake/StockTakeModals';
import StockTakeHistory from './stocktake/StockTakeHistory';
import FreeScanTable from './stocktake/FreeScanTable';
import ICDocumentTable from './stocktake/ICDocumentTable';
import UndoBar from './stocktake/UndoBar';
import useMediaQuery from './stocktake/useMediaQuery';
import LastScanPanel from './common/LastScanPanel';
import Meter from './common/Meter';
import {
  buildIcLines,
  countStatuses,
  parseQuantity,
  quantityNeedsConfirm,
  subtractSubmitted,
  formatUkTime,
} from './stocktake/stockTakeLogic';

const STORAGE_KEY = 'stockTake_ongoingItems';
const IC_SESSION_KEY = 'stockTake_icSession';
const SESSION_ID_KEY = 'stockTake_sessionId';  // Server-side session ID
const IC_SCANS_KEY = 'stockTake_icScans';  // Local backup of IC scanned counts

// localStorage can throw (private mode, quota), so every access is guarded.
const storageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.error(`Error reading ${key}:`, e);
    return null;
  }
};

const storageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.error(`Error saving ${key}:`, e);
  }
};

const storageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error(`Error removing ${key}:`, e);
  }
};

const readJson = (key) => {
  try {
    const raw = storageGet(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error(`Error parsing ${key}:`, e);
    return null;
  }
};

// Server session format { barcode: { count, timestamp } } <-> Map of scanned items
const scansObjectToMap = (obj) => new Map(
  Object.entries(obj || {}).map(([barcode, data]) => [
    barcode,
    { barcode, count: data.count, lastScanned: new Date(data.timestamp || Date.now()) }
  ])
);

const scansMapToObject = (map) => {
  const obj = {};
  map.forEach((item, barcode) => {
    obj[barcode] = { count: item.count, timestamp: item.lastScanned.toISOString() };
  });
  return obj;
};

// Pick between the server session's scans and the local backup for the same document.
// Counts can go down (decrement, remove, clear), so the copies are never merged per barcode:
// whichever copy changed most recently wins as a whole. The local copy is only newer when
// a change never reached the server (offline, tab closed before the debounced save).
const pickLatestScans = (serverScannedItems, serverUpdatedAt, docNo) => {
  const local = readJson(IC_SCANS_KEY);
  const serverTime = Date.parse(serverUpdatedAt || '') || 0;
  if (local && docNo && local.docNo === docNo && (local.savedAt || 0) > serverTime) {
    return scansObjectToMap(local.items);
  }
  return scansObjectToMap(serverScannedItems);
};

// Free-scan progress backup (array of { barcode, count, lastScanned })
const loadFreeScans = () => {
  const parsed = readJson(STORAGE_KEY);
  if (!Array.isArray(parsed)) return new Map();
  return new Map(parsed.map(item => [item.barcode, { ...item, lastScanned: new Date(item.lastScanned) }]));
};

const loadIcSession = () => {
  const parsed = readJson(IC_SESSION_KEY);
  return parsed?.icDocItems && parsed?.icDocInfo ? parsed : null;
};

// Local backup of IC counts - only valid for the document it was taken for
const loadIcScans = (docNo) => {
  const parsed = readJson(IC_SCANS_KEY);
  return parsed && docNo && parsed.docNo === docNo ? scansObjectToMap(parsed.items) : new Map();
};

const makeIdempotencyKey = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

// What was put back on screen from this device's backup on load (shown as a notice)
const describeLocalRestore = (icSession, icScans, freeScans) => {
  if (icSession) {
    return `Restored IC count ${icSession.icDocInfo.docNo} from this device: ${icScans.size} of ${icSession.icDocItems.length} lines counted.`;
  }
  if (freeScans.size > 0) {
    return `Restored ${freeScans.size} free-scan barcode${freeScans.size !== 1 ? 's' : ''} from this device.`;
  }
  return null;
};

// Why a scan isn't being counted right now
const PAUSED_MESSAGES = {
  off: 'Scanner is off. Turn it on to count.',
  submitting: 'Submitting. Scan again once it has finished.',
  history: 'Scanning is paused in History. Switch to Scan Items to count.',
  modal: 'Close the open dialog first. That scan was not counted.',
};

// Server save retry back-off: 5s, 10s, 20s, then every 30s
const retryDelay = (attempt) => Math.min(30000, 5000 * 2 ** Math.max(0, attempt - 1));

const StockTake = () => {
  // Saved progress is restored synchronously from localStorage on the first render, so
  // the backup effects below never run against an empty state and wipe the saved data
  // before it has been read. A server IC session (if any) is merged in afterwards.
  const [initialIcSession] = useState(loadIcSession);
  const icDocInputId = useId();

  // View mode: 'scan' for scanning, 'history' for viewing submitted stock takes
  const [viewMode, setViewMode] = useState('scan');

  // Scan mode: 'free' for free scanning, 'icDocument' for IC document-based
  const [scanMode, setScanMode] = useState(initialIcSession ? 'icDocument' : 'free');

  // IC Document state
  const [icDocNo, setIcDocNo] = useState(initialIcSession?.icDocInfo.docNo || '');
  const [icDocItems, setIcDocItems] = useState(initialIcSession?.icDocItems || []); // Items from loaded IC document
  const [isLoadingIcDoc, setIsLoadingIcDoc] = useState(false);
  const [icDocLoaded, setIcDocLoaded] = useState(Boolean(initialIcSession));
  const [icDocInfo, setIcDocInfo] = useState(initialIcSession?.icDocInfo || null); // { docNo, curDate, warehouse, location }
  const [sessionId, setSessionId] = useState(() => storageGet(SESSION_ID_KEY));  // Server-side session ID for persistence
  // False until the saved server session has been checked; server saves wait for it so
  // a stale local copy can't overwrite newer server counts during restore.
  const [sessionReady, setSessionReady] = useState(false);
  // "Count already in progress" choice: { result, docNo, lineCount, countedLines, updatedAt, existing }
  const [resumePrompt, setResumePrompt] = useState(null);

  // Warehouse for stock take (always FG / RM PICK AREA)
  const selectedWarehouse = 'FG';

  // Scanned items stored as Map: barcode -> { barcode, count, lastScanned }.
  // Free-scan and IC-document scans are kept in SEPARATE maps so switching modes never
  // mixes them (an IC scan must never be submitted as a free-scan adjustment, and loading
  // an IC document must not wipe free-scan progress). The active map is selected by mode.
  const [freeScannedItems, setFreeScannedItems] = useState(loadFreeScans);
  const [icScannedItems, setIcScannedItems] = useState(() => loadIcScans(initialIcSession?.icDocInfo.docNo));
  // Active map for the current mode. All generic reads go through it.
  const scannedItems = scanMode === 'icDocument' ? icScannedItems : freeScannedItems;
  const [scanActive, setScanActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  // { key, signature } for the submission in flight / awaiting retry. The key is reused
  // only while the outcome is unknown (network failure) and the data is unchanged.
  const submitAttemptRef = useRef(null);

  // Quantity popup state (for IC Document mode). The quantity is kept as the field's
  // text so it can be empty while the operator retypes it.
  const [showQuantityPopup, setShowQuantityPopup] = useState(false);
  const [popupItem, setPopupItem] = useState(null); // The IC doc item being scanned
  const [popupQuantityText, setPopupQuantityText] = useState('1'); // Quantity to add

  // Line correction dialog: { barcode, description, expected?, counted, mode }
  const [editLine, setEditLine] = useState(null);
  // Danger confirmation: 'clearFree' | 'clearCounts' | 'closeDocument'
  const [dangerAction, setDangerAction] = useState(null);
  // Removed free-scan item that can still be put back: { id, message, item }
  const [undo, setUndo] = useState(null);

  // Result of the latest scan, shown next to the scan input until the next one
  const [lastScan, setLastScan] = useState(null);
  // IC line to scroll to and flash: { barcode, key }
  const [highlight, setHighlight] = useState(null);

  // Notice about progress restored on load (from this device or the server)
  const [restoreNotice, setRestoreNotice] = useState(() =>
    describeLocalRestore(initialIcSession, icScannedItems, freeScannedItems)
  );
  // Server save state of the IC count: idle | saving | saved | error
  const [saveStatus, setSaveStatus] = useState({ state: 'idle', at: null });

  // History state
  const [historyData, setHistoryData] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [historySearchTerm, setHistorySearchTerm] = useState('');

  const { showToast, toastProps } = useToast();

  // Layout by viewport width (not device type): narrow tables become cards in CSS
  const isCompact = useMediaQuery(MEDIA.tablet);
  const isPhone = useMediaQuery(MEDIA.phone);

  // Scanning pauses while any dialog other than the quantity popup is open (that one
  // takes repeat scans), in History, and while a submission is in flight.
  const openModalCount = useModalOpenCount();
  const otherModalOpen = openModalCount - (showQuantityPopup ? 1 : 0) > 0;
  const pauseReason = !scanActive ? 'off'
    : isSubmitting ? 'submitting'
    : viewMode !== 'scan' ? 'history'
    : otherModalOpen ? 'modal'
    : null;
  const pauseReasonRef = useRef(pauseReason);
  pauseReasonRef.current = pauseReason;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Adopt an active server session: its document and its scans merged with any local
  // backup for the same document.
  const applyServerSession = useCallback((id, session) => {
    const scans = pickLatestScans(session.scannedItems, session.updatedAt, session.docNo);
    if (session.items && session.items.length > 0) {
      setIcDocItems(session.items);
      setIcDocInfo({
        docNo: session.docNo,
        curDate: session.curDate,
        warehouse: session.warehouse,
        location: session.location
      });
      setIcDocNo(session.docNo || '');
      setIcDocLoaded(true);
      setScanMode('icDocument');
      const saved = session.updatedAt ? `, last saved ${formatUkTime(session.updatedAt)}` : '';
      setRestoreNotice(`Resumed IC count ${session.docNo}: ${scans.size} of ${session.items.length} lines counted${saved}.`);
    }
    setIcScannedItems(scans);
    setSessionId(id);
    if (session.updatedAt) setSaveStatus({ state: 'saved', at: session.updatedAt });
  }, []);

  // Check the saved server session on mount. Local progress is already on screen.
  useEffect(() => {
    let cancelled = false;

    const restoreServerSession = async () => {
      const savedSessionId = storageGet(SESSION_ID_KEY);
      if (savedSessionId) {
        const result = await getSession(savedSessionId);
        if (cancelled) return;
        if (result.success && result.session && result.session.status === 'active') {
          applyServerSession(savedSessionId, result.session);
        } else if (result.success || result.status === 404) {
          // Session completed, abandoned or gone - stop saving to it
          setSessionId(null);
          storageRemove(SESSION_ID_KEY);
        } else {
          // Server unreachable: keep the session id so later scans still save once it
          // is back, and carry on with the local backup.
          console.warn('Could not restore server session, using local backup:', result.message);
          setSaveStatus({ state: 'error', at: null });
        }
      }
      if (!cancelled) setSessionReady(true);
    };

    restoreServerSession();
    return () => {
      cancelled = true;
    };
  }, [applyServerSession]);

  // Save IC session to localStorage
  useEffect(() => {
    if (icDocLoaded && icDocItems.length > 0 && icDocInfo) {
      storageSet(IC_SESSION_KEY, JSON.stringify({
        icDocItems,
        icDocInfo
      }));
    }
    // Save sessionId if we have one
    if (sessionId) {
      storageSet(SESSION_ID_KEY, sessionId);
    }
  }, [icDocItems, icDocInfo, icDocLoaded, sessionId]);

  // Back up IC scanned counts locally too, so they survive a failed/pending server save
  useEffect(() => {
    if (icDocLoaded && icDocInfo?.docNo) {
      const items = scansMapToObject(icScannedItems);
      const previous = readJson(IC_SCANS_KEY);
      // savedAt marks when the counts last changed, not when this effect last ran, so a
      // re-render or reload can't make a stale backup look newer than the server copy.
      const unchanged = previous && previous.docNo === icDocInfo.docNo
        && JSON.stringify(previous.items) === JSON.stringify(items);
      storageSet(IC_SCANS_KEY, JSON.stringify({
        docNo: icDocInfo.docNo,
        items,
        savedAt: unchanged ? previous.savedAt : Date.now()
      }));
    }
  }, [icDocLoaded, icDocInfo, icScannedItems]);

  // Save scanned items to server (debounced). Server sessions track the IC-document
  // scans, so this persists the IC map only. A pending save is flushed - not dropped -
  // when the component unmounts or the page is hidden, and a failed save is retried
  // (unless a newer change has replaced it) with the result shown in the save chip.
  const saveTimeoutRef = useRef(null);
  const pendingSaveRef = useRef(null); // { sessionId, scannedItems } awaiting save
  const saveAttemptsRef = useRef(0);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const flushPendingSave = useCallback((options) => {
    clearTimeout(saveTimeoutRef.current);
    const pending = pendingSaveRef.current;
    if (!pending) return;
    pendingSaveRef.current = null;
    if (mountedRef.current) setSaveStatus(s => ({ ...s, state: 'saving' }));
    saveScanProgress(pending.sessionId, pending.scannedItems, options).then(result => {
      if (!mountedRef.current) return;
      if (result.success) {
        saveAttemptsRef.current = 0;
        if (!pendingSaveRef.current) {
          setSaveStatus({ state: 'saved', at: result.updatedAt || new Date().toISOString() });
        }
        return;
      }
      console.warn('Failed to save progress to server:', result.message);
      setSaveStatus(s => ({ ...s, state: 'error' }));
      // Retry this save unless a newer one replaced it or the session has ended
      if (!pendingSaveRef.current && sessionIdRef.current === pending.sessionId) {
        pendingSaveRef.current = pending;
        saveAttemptsRef.current += 1;
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => flushPendingSave(), retryDelay(saveAttemptsRef.current));
      }
    });
  }, []);

  useEffect(() => {
    if (!sessionId || !sessionReady) {
      // Session cleared/submitted: discard any save queued for it
      clearTimeout(saveTimeoutRef.current);
      pendingSaveRef.current = null;
      return;
    }

    pendingSaveRef.current = { sessionId, scannedItems: scansMapToObject(icScannedItems) };

    // Debounce server saves - wait 2 seconds after last change
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => flushPendingSave(), 2000);
  }, [sessionId, sessionReady, icScannedItems, flushPendingSave]);

  useEffect(() => {
    const handlePageHide = () => flushPendingSave({ keepalive: true });
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      flushPendingSave();
    };
  }, [flushPendingSave]);

  // Back up free-scan progress to localStorage whenever it changes
  useEffect(() => {
    if (freeScannedItems.size > 0) {
      const itemsArray = Array.from(freeScannedItems.values());
      storageSet(STORAGE_KEY, JSON.stringify(itemsArray));
    } else {
      storageRemove(STORAGE_KEY);
    }
  }, [freeScannedItems]);

  const reportLastScan = useCallback((status, title, detail, meta) => {
    setLastScan({ status, title, detail, meta, at: Date.now() });
  }, []);

  // A scan that arrives while scanning is paused or the scanner is off
  const handleScanWhileDisabled = useCallback((code) => {
    const reason = pauseReasonRef.current || 'off';
    scanFeedback('error');
    showToast(PAUSED_MESSAGES[reason], 'error', { duration: 6000 });
    setLastScan({ status: 'error', title: 'Not counted', detail: code, meta: PAUSED_MESSAGES[reason], at: Date.now() });
  }, [showToast]);

  // Add `quantity` to an IC line's count
  const addToIcCount = useCallback((barcode, quantity) => {
    setIcScannedItems(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(barcode);
      newMap.set(barcode, { barcode, count: (existing ? existing.count : 0) + quantity, lastScanned: new Date() });
      return newMap;
    });
  }, []);

  // Handle barcode scan - accumulate counts. `info` is set when the scan landed in a
  // data-scan-capture field (the quantity popup): info.previousValue is what that field
  // held before the scanner typed into it.
  const handleScan = useCallback((barcode, info) => {
    if (!barcode || barcode.trim() === '') return;

    // Typed into the scan box while paused (the global listener is already off)
    if (pauseReasonRef.current) {
      handleScanWhileDisabled(barcode.trim());
      return;
    }

    const scanned = barcode.trim();

    // In IC Document mode, show quantity popup if barcode matches an item. Counts are
    // keyed by the document's own barcode so the table and submission find them, and
    // matching tolerates the UPC-A/EAN-13 leading-zero difference.
    if (scanMode === 'icDocument') {
      if (!icDocLoaded) {
        scanFeedback('error');
        reportLastScan('error', 'No IC document loaded', scanned, 'Load the document first, then scan.');
        return;
      }

      const matchedItem = icDocItems.find(item => barcodesMatch(scanned, item.barcode));
      if (matchedItem) {
        const docBarcode = matchedItem.barcode;
        const name = matchedItem.description || matchedItem.partName || docBarcode;

        // If popup is already open for this same item, increment quantity
        if (showQuantityPopup && popupItem && popupItem.barcode === docBarcode) {
          setPopupQuantityText(prev => String((parseQuantity(prev) || 0) + 1));
          scanFeedback('success');
          reportLastScan('success', name, docBarcode, 'Added 1 more to the quantity');
          return;
        }

        // If a popup is open for a DIFFERENT item, commit its pending quantity first so
        // it isn't silently discarded when we switch the popup to the newly scanned item.
        // A quantity that would need confirming is not committed unconfirmed.
        if (showQuantityPopup && popupItem && popupItem.barcode !== docBarcode) {
          const prevQty = parseQuantity(info?.previousValue !== undefined ? info.previousValue : popupQuantityText);
          const prevName = popupItem.description || popupItem.partName || popupItem.barcode;
          if (prevQty >= 1 && !quantityNeedsConfirm(popupItem.currentScanned + prevQty, popupItem.quantity)) {
            addToIcCount(popupItem.barcode, prevQty);
            showToast(`Added ${prevQty} × ${prevName}`, 'success');
          } else if (prevQty >= 1) {
            showToast(`${prevQty} × ${prevName} was NOT added: it needed confirming. Scan it again.`, 'error');
          }
        }

        // Get current scanned count for this item
        const existing = icScannedItems.get(docBarcode);
        const currentCount = existing ? existing.count : 0;
        const alreadyComplete = matchedItem.quantity > 0 && currentCount >= matchedItem.quantity;

        scanFeedback(alreadyComplete ? 'duplicate' : 'success');
        reportLastScan(
          alreadyComplete ? 'warning' : 'success',
          name,
          docBarcode,
          alreadyComplete
            ? `Already counted ${currentCount} of ${matchedItem.quantity}. Check before adding more.`
            : `Counted ${currentCount} of ${matchedItem.quantity} so far`
        );
        setHighlight({ barcode: docBarcode, key: Date.now() });

        setPopupItem({
          ...matchedItem,
          currentScanned: currentCount
        });
        setPopupQuantityText('1'); // Default to adding 1
        setShowQuantityPopup(true);
        return;
      }

      // Barcode not found in IC document
      scanFeedback('error');
      reportLastScan('error', 'Not on this IC document', scanned, 'Not counted');
      showToast(`Barcode not in document: ${scanned}`, 'error');
      return;
    }

    // Handle UPC-A to EAN-13 conversion (same as order picking feature)
    // If barcode is exactly 12 digits (UPC-A format where scanner dropped leading zero),
    // add leading zero to convert to EAN-13 format that Priority ERP expects
    const trimmedBarcode = scanned.length === 12 && /^\d+$/.test(scanned) ? '0' + scanned : scanned;

    // Free scan mode - directly update count
    const newCount = (freeScannedItems.get(trimmedBarcode)?.count || 0) + 1;
    setFreeScannedItems(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(trimmedBarcode);
      newMap.set(trimmedBarcode, {
        barcode: trimmedBarcode,
        count: existing ? existing.count + 1 : 1,
        lastScanned: new Date()
      });
      return newMap;
    });

    scanFeedback('success');
    reportLastScan('success', trimmedBarcode, `Count now ${newCount}`, newCount === 1 ? 'New barcode' : null);
  }, [scanMode, icDocLoaded, icDocItems, icScannedItems, freeScannedItems, showQuantityPopup, popupItem, popupQuantityText, addToIcCount, handleScanWhileDisabled, reportLastScan, showToast]);

  // Global keyboard listener for hardware barcode scanners
  useBarcodeBuffer(handleScan, { active: !pauseReason, onKeyWhileDisabled: handleScanWhileDisabled });

  // Free scan: remove an item, with Undo
  const removeItem = (barcode) => {
    const item = freeScannedItems.get(barcode);
    if (!item) return;
    setFreeScannedItems(prev => {
      const newMap = new Map(prev);
      newMap.delete(barcode);
      return newMap;
    });
    setUndo({ id: Date.now(), message: `Removed ${barcode} (${item.count})`, item });
  };

  const undoRemove = () => {
    if (!undo) return;
    const { item } = undo;
    setFreeScannedItems(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(item.barcode);
      // Scanned again since it was removed: keep those scans on top of the restored count
      newMap.set(item.barcode, current ? { ...current, count: current.count + item.count } : item);
      return newMap;
    });
    setUndo(null);
    showToast(`Restored ${item.barcode}`, 'success');
  };

  // Increment count for a barcode (free scan)
  const incrementCount = (barcode) => {
    setFreeScannedItems(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(barcode);
      if (item) {
        newMap.set(barcode, { ...item, count: item.count + 1 });
      }
      return newMap;
    });
  };

  // Decrement count for a barcode (free scan). Going below 1 removes it, with Undo.
  const decrementCount = (barcode) => {
    const item = freeScannedItems.get(barcode);
    if (!item) return;
    if (item.count <= 1) {
      removeItem(barcode);
      return;
    }
    setFreeScannedItems(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(barcode);
      if (current) newMap.set(barcode, { ...current, count: Math.max(1, current.count - 1) });
      return newMap;
    });
  };

  // Line correction dialog
  const openIcLineEdit = (line) => {
    setEditLine({
      mode: 'ic',
      barcode: line.barcode,
      description: line.description || line.partName || '',
      expected: line.expectedCount,
      counted: line.scannedCount,
    });
  };

  const openFreeLineEdit = (item) => {
    setEditLine({ mode: 'free', barcode: item.barcode, counted: item.count });
  };

  const saveLineEdit = (newCount) => {
    const line = editLine;
    setEditLine(null);
    if (!line) return;
    if (line.mode === 'free' && newCount === 0) {
      removeItem(line.barcode);
      return;
    }
    const setMap = line.mode === 'ic' ? setIcScannedItems : setFreeScannedItems;
    setMap(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(line.barcode);
      if (newCount === 0) {
        newMap.delete(line.barcode);
      } else {
        // Keep lastScanned on free-scan rows so the row doesn't jump in the list
        newMap.set(line.barcode, {
          barcode: line.barcode,
          count: newCount,
          lastScanned: existing && line.mode === 'free' ? existing.lastScanned : new Date()
        });
      }
      return newMap;
    });
    if (line.mode === 'ic') setHighlight({ barcode: line.barcode, key: Date.now() });
    showToast(`${line.description || line.barcode}: count set to ${newCount}`, 'success');
  };

  // Clear IC document session (after the danger confirmation)
  const clearIcSession = () => {
    // Mark session as abandoned in database (fire and forget)
    if (sessionId) {
      abandonSession(sessionId).then(result => {
        if (!result.success) {
          console.warn('Failed to mark session as abandoned:', result.message);
        }
      });
    }

    setIcDocItems([]);
    setIcDocInfo(null);
    setIcDocNo('');
    setIcDocLoaded(false);
    setIcScannedItems(new Map());
    setSessionId(null);
    setSaveStatus({ state: 'idle', at: null });
    setHighlight(null);
    setLastScan(null);
    setRestoreNotice(null);
    storageRemove(IC_SESSION_KEY);
    storageRemove(SESSION_ID_KEY);
    storageRemove(IC_SCANS_KEY);
    showToast('IC document closed. Its counts were discarded.', 'warning');
  };

  const DANGER_ACTIONS = {
    clearFree: {
      title: 'Clear all scanned items?',
      message: `This removes all ${freeScannedItems.size} free-scan barcode${freeScannedItems.size !== 1 ? 's' : ''} and their counts.`,
      confirmLabel: 'Clear all items',
      run: () => {
        setFreeScannedItems(new Map());
        setLastScan(null);
        showToast('All free-scan items cleared', 'warning');
      },
    },
    clearCounts: {
      title: 'Reset all counts?',
      message: `Every count for ${icDocInfo?.docNo || 'this document'} goes back to 0. The document stays loaded.`,
      confirmLabel: 'Reset counts',
      run: () => {
        setIcScannedItems(new Map());
        setLastScan(null);
        showToast('All IC document counts cleared', 'warning');
      },
    },
    closeDocument: {
      title: 'Close this IC document?',
      message: `${icDocInfo?.docNo || 'The document'} is unloaded and its counts are discarded. Nothing is sent to Priority.`,
      confirmLabel: 'Close document',
      run: clearIcSession,
    },
  };

  const confirmDangerAction = () => {
    const action = DANGER_ACTIONS[dangerAction];
    setDangerAction(null);
    action?.run();
  };

  // Put a loaded IC document on screen, either resuming an existing session
  // ({ sessionId, scans }) or starting fresh with the session the load created.
  const applyLoadedDocument = (result, resumed) => {
    setIcDocItems(result.items);
    setIcDocInfo({
      docNo: result.docNo,
      curDate: result.curDate,
      warehouse: result.warehouse,
      location: result.location
    });
    setIcDocLoaded(true);
    setHighlight(null);
    setLastScan(null);
    setRestoreNotice(null);

    if (resumed) {
      setIcScannedItems(resumed.scans);
      setSessionId(resumed.sessionId);
      setSaveStatus({ state: 'saved', at: resumed.updatedAt || null });
      showToast(`Resumed count for ${result.docNo} (${result.items.length} lines)`, 'success');
      return;
    }

    setIcScannedItems(new Map()); // Fresh IC scans for this document (free scans preserved)
    setSaveStatus({ state: 'idle', at: null });

    // Store server-side session ID for persistence
    if (result.sessionId) {
      setSessionId(result.sessionId);
      storageSet(SESSION_ID_KEY, result.sessionId);
    } else {
      setSessionId(null);
    }

    showToast(`Loaded ${result.items.length} lines from ${result.docNo}`, 'success');
  };

  // Load IC document from Priority
  const loadIcDocument = async () => {
    if (!icDocNo.trim()) {
      showToast('Please enter a document number', 'error');
      return;
    }

    setIsLoadingIcDoc(true);
    try {
      const result = await getICDocument(icDocNo.trim());
      if (result.success && result.items.length > 0) {
        // An active session already exists for this document (another tab/device or an
        // earlier visit). get-ic-document always opens a NEW session too, so if the
        // operator resumes the existing one the new one is abandoned; if they start new,
        // the new session is used and the existing one is left alone. The choice is
        // resolved before touching state so the previous document's counts are never
        // backed up under this document's number.
        if (result.existingSessionId) {
          const existing = await getSession(result.existingSessionId);
          const session = existing.success ? existing.session : null;
          setResumePrompt({
            result,
            existing,
            docNo: result.docNo,
            lineCount: result.items.length,
            countedLines: session?.scannedItems
              ? Object.keys(session.scannedItems).length
              : (result.existingScannedItemCount ?? null),
            updatedAt: session?.updatedAt || null,
          });
          return;
        }
        applyLoadedDocument(result, null);
      } else if (result.success && result.items.length === 0) {
        showToast('Document found but contains no items', 'warning');
      } else {
        showToast(result.message || 'Failed to load IC document', 'error');
      }
    } catch (error) {
      console.error('Error loading IC document:', error);
      showToast('Failed to load IC document', 'error');
    } finally {
      setIsLoadingIcDoc(false);
    }
  };

  const resolveResumePrompt = (resume) => {
    const prompt = resumePrompt;
    setResumePrompt(null);
    if (!prompt) return;
    const { result, existing } = prompt;

    if (!resume) {
      applyLoadedDocument(result, null);
      return;
    }

    let resumed = null;
    if (existing.success && existing.session && existing.session.status === 'active') {
      resumed = {
        sessionId: result.existingSessionId,
        scans: pickLatestScans(existing.session.scannedItems, existing.session.updatedAt, result.docNo),
        updatedAt: existing.session.updatedAt,
      };
    } else if (!existing.success && existing.status !== 404) {
      // Couldn't read it right now - keep using it with whatever is backed up locally
      resumed = { sessionId: result.existingSessionId, scans: loadIcScans(result.docNo), updatedAt: null };
    }

    if (resumed && result.sessionId && result.sessionId !== resumed.sessionId) {
      abandonSession(result.sessionId).then(abandoned => {
        if (!abandoned.success) console.warn('Failed to abandon unused session:', abandoned.message);
      });
    }
    if (!resumed) {
      showToast('That count has already ended, so a new count was started.', 'warning');
    }
    applyLoadedDocument(result, resumed);
  };

  // Handle scan mode change - allow switching without losing data
  const handleScanModeChange = (mode) => {
    setScanMode(mode);
    setLastScan(null);
    // IC document data persists in the background when switching to free mode
    // User can switch back to IC mode and continue where they left off
  };

  // Load history from Priority ERP
  const loadHistory = async () => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const result = await getStockTakeHistory();
      if (result.success) {
        setHistoryData(result.documents || []);
      } else {
        setHistoryError(result.message || 'Failed to load history');
      }
    } catch (error) {
      console.error('Error loading history:', error);
      setHistoryError('Failed to load history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Toggle view mode
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'history') {
      loadHistory();
    }
    setSelectedDocument(null);
  };

  // Handle submit button click - show the review dialog
  const handleSubmitClick = () => {
    if (scanMode === 'icDocument') {
      if (!icDocLoaded || icDocItems.length === 0) {
        showToast('No IC document loaded', 'error');
        return;
      }
    } else if (freeScannedItems.size === 0) {
      showToast('No items to submit', 'error');
      return;
    }
    setShowConfirmModal(true);
  };

  // Confirm and submit stock take
  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    let submittedItems;
    let submittedUniqueItems;
    let submittedTotalCount;

    // In IC document mode, submit all items from the document
    // Items not scanned = count 0 (missing stock)
    if (scanMode === 'icDocument' && icDocLoaded) {
      submittedItems = icDocItems.map(item => {
        const scannedItem = scannedItems.get(item.barcode);
        return {
          barcode: item.barcode,
          count: scannedItem ? scannedItem.count : 0,
          kline: item.kline  // Include line number for Priority PATCH updates
        };
      });
      // The stock-take function calculates the difference from the scanned counts
      submittedUniqueItems = submittedItems.length;
      submittedTotalCount = submittedItems.reduce((sum, item) => sum + item.count, 0);
    } else {
      // Free scan mode - use scanned items directly
      submittedItems = Array.from(scannedItems.values()).map(item => ({
        barcode: item.barcode,
        count: item.count
      }));
      submittedUniqueItems = scannedItems.size;
      submittedTotalCount = submittedItems.reduce((sum, item) => sum + item.count, 0);
    }

    try {
      // Pass IC document number when in IC document mode (for PATCH instead of POST)
      const icDocNumber = (scanMode === 'icDocument' && icDocInfo?.docNo) ? icDocInfo.docNo : null;
      // Pass sessionId for server-side session tracking and warehouse for stock check
      // Reuse the idempotency key only when retrying the exact same submission after an
      // unknown outcome; any change to the data (or a definite answer) gets a fresh key.
      const signature = JSON.stringify({ scanMode, icDocNumber, submittedItems });
      if (!submitAttemptRef.current || submitAttemptRef.current.signature !== signature) {
        submitAttemptRef.current = { key: makeIdempotencyKey(), signature };
      }
      const result = await submitStockTake(submittedItems, icDocNumber, sessionId, selectedWarehouse, submitAttemptRef.current.key);
      // The server only records successful submissions under a key, so reusing it on a
      // retry of the same data after any failure is safe; retire it once it succeeded.
      if (result.success) {
        submitAttemptRef.current = null;
      }

      // An earlier attempt with this key is still running - don't show it as a failure
      if (result.isConflict) {
        showToast(result.message, 'warning');
        return;
      }

      // A duplicate means an earlier attempt already went through - same as success.
      if (result.success) {
        // Extract document number from Priority response (or use the IC doc number)
        const docNo = icDocNumber ||
                      result.response?.docNo ||
                      result.response?.priorityResponse?.DOCNO ||
                      result.response?.DOCNO ||
                      'N/A';
        setSubmissionResult({
          success: true,
          uniqueItems: submittedUniqueItems,
          totalCount: submittedTotalCount,
          documentNumber: docNo,
          // Scanned barcodes the server could not submit (e.g. not found in Priority);
          // listed in the success dialog so they can't be missed
          skippedBarcodes: result.response?.skippedBarcodes || []
        });
        setShowSuccessModal(true);
        setLastScan(null);
        setHighlight(null);
        // Clear only the submitted mode's scans/backup so the other mode's progress survives.
        if (scanMode === 'icDocument') {
          setIcScannedItems(new Map());
          setIcDocItems([]);
          setIcDocInfo(null);
          setIcDocNo('');
          setIcDocLoaded(false);
          setSessionId(null);
          setSaveStatus({ state: 'idle', at: null });
          storageRemove(IC_SESSION_KEY);
          storageRemove(SESSION_ID_KEY);
          storageRemove(IC_SCANS_KEY);
        } else {
          // Only the submitted counts come off, so nothing scanned meanwhile is lost
          setFreeScannedItems(prev => subtractSubmitted(prev, submittedItems));
        }
      } else {
        // Extract Priority's actual error from the response
        let displayMessage = result.message || 'Priority ERP rejected the submission';

        // Try to extract more specific error from Priority response
        if (result.priorityResponse) {
          const priorityResp = result.priorityResponse;
          // Check for InterfaceErrors in Priority response
          if (priorityResp?.FORM?.InterfaceErrors?.text) {
            displayMessage = priorityResp.FORM.InterfaceErrors.text;
          } else if (priorityResp?.error?.message) {
            displayMessage = priorityResp.error.message;
          } else if (typeof priorityResp === 'string') {
            displayMessage = priorityResp;
          }
        }

        // Check for barcodes not found in current stock
        const notFoundBarcodes = result.currentStock
          ? submittedItems.filter(item => !(item.barcode in result.currentStock))
          : [];

        setErrorDetails({
          message: result.isNetworkError
            ? 'Could not reach the stock take service. Check your connection and try again.'
            : displayMessage,
          rawError: result.message,
          submittedItems: submittedItems,
          priorityResponse: result.priorityResponse,
          priorityStatus: result.priorityStatus,
          notFoundBarcodes: notFoundBarcodes,
          isNetworkError: result.isNetworkError
        });
        setShowErrorModal(true);
      }
    } catch (error) {
      setErrorDetails({
        message: 'Failed to connect to stock take service',
        rawError: error.message,
        submittedItems: submittedItems,
        isNetworkError: true
      });
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close success modal
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSubmissionResult(null);
  };

  // Close error modal
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
  };

  // Quantity popup confirm - add the (already validated) quantity to the count
  const handleQuantityConfirm = (quantityToAdd) => {
    if (!popupItem || !(quantityToAdd >= 1)) return;

    const barcode = popupItem.barcode;
    const newTotal = popupItem.currentScanned + quantityToAdd;
    addToIcCount(barcode, quantityToAdd);

    const itemName = popupItem.description || popupItem.partName || barcode;
    reportLastScan(
      newTotal > popupItem.quantity ? 'warning' : 'success',
      `Added ${quantityToAdd} × ${itemName}`,
      barcode,
      `Now ${newTotal} of ${popupItem.quantity}${newTotal > popupItem.quantity ? ` (over by ${newTotal - popupItem.quantity})` : ''}`
    );
    setHighlight({ barcode, key: Date.now() });

    // Close popup and reset state
    setShowQuantityPopup(false);
    setPopupItem(null);
    setPopupQuantityText('1');
  };

  // Handle quantity popup cancel
  const handleQuantityCancel = () => {
    if (popupItem) {
      reportLastScan('warning', 'Not added', popupItem.description || popupItem.partName || popupItem.barcode, 'Quantity cancelled');
    }
    setShowQuantityPopup(false);
    setPopupItem(null);
    setPopupQuantityText('1');
  };

  // Calculate totals
  const uniqueItems = scannedItems.size;
  const totalCount = Array.from(scannedItems.values()).reduce((sum, item) => sum + item.count, 0);

  // Format time for display (UK)
  const formatTime = (date) => formatUkTime(date, { seconds: !isCompact });

  // Convert Map to array for rendering
  const itemsArray = Array.from(scannedItems.values());

  // IC document progress: a line counts as counted once it has any scans
  const icItemsWithStatus = scanMode === 'icDocument' && icDocLoaded ? buildIcLines(icDocItems, icScannedItems) : [];
  const icStatusCounts = countStatuses(icItemsWithStatus);
  const icCountedLines = icItemsWithStatus.length - icStatusCounts.not_scanned;

  const showBottomBar = viewMode === 'scan' && (scanMode === 'free' || icDocLoaded);

  // Save chip for the IC count (server-backed); free scans only live on this device
  const renderSaveChip = () => {
    if (!sessionId) {
      return <span className="st-save-chip is-local"><CloudOff size={16} aria-hidden="true" /> Saved on this device only</span>;
    }
    if (saveStatus.state === 'saving') {
      return <span className="st-save-chip is-saving"><Loader size={16} aria-hidden="true" /> Saving…</span>;
    }
    if (saveStatus.state === 'error') {
      return <span className="st-save-chip is-error"><CloudOff size={16} aria-hidden="true" /> Not saved, retrying</span>;
    }
    if (saveStatus.state === 'saved') {
      return <span className="st-save-chip is-saved"><Cloud size={16} aria-hidden="true" /> Saved {formatUkTime(saveStatus.at)}</span>;
    }
    return null;
  };

  return (
    <div className={`stock-take-container ${showBottomBar ? 'has-bottom-bar' : ''}`.trim()}>
      {/* Toast notifications */}
      <Toast {...toastProps} position="top-center" />

      {/* Header */}
      <div className="stock-take-header">
        <h2>Stock Take</h2>
        <div className="stock-take-header-actions">
          <div className="view-toggle-buttons" role="group" aria-label="View">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'scan' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('scan')}
              aria-pressed={viewMode === 'scan'}
            >
              <ScanLine size={16} aria-hidden="true" /> Scan Items
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'history' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('history')}
              aria-pressed={viewMode === 'history'}
            >
              <History size={16} aria-hidden="true" /> View History
            </button>
          </div>
        </div>
      </div>

      {/* Scan View */}
      {viewMode === 'scan' && (
        <>
          {restoreNotice && (
            <div className="st-restore-notice" role="status">
              <Info size={20} aria-hidden="true" />
              <p>{restoreNotice}</p>
              <button type="button" className="st-icon-btn" onClick={() => setRestoreNotice(null)} aria-label="Dismiss notice">
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Scan Mode Toggle */}
          <div className="scan-mode-toggle" role="group" aria-label="Scan mode">
            <button
              type="button"
              className={`scan-mode-btn ${scanMode === 'free' ? 'active' : ''}`}
              onClick={() => handleScanModeChange('free')}
              aria-pressed={scanMode === 'free'}
            >
              Free Scan
            </button>
            <button
              type="button"
              className={`scan-mode-btn ${scanMode === 'icDocument' ? 'active' : ''} ${icDocLoaded ? 'has-session' : ''}`}
              onClick={() => handleScanModeChange('icDocument')}
              aria-pressed={scanMode === 'icDocument'}
            >
              From IC Document {icDocLoaded && scanMode !== 'icDocument' && (
                <><Circle size={8} fill="currentColor" aria-hidden="true" /><span className="sr-only"> (in progress)</span></>
              )}
            </button>
          </div>

          {/* Notice when IC document is loaded but in free scan mode */}
          {scanMode === 'free' && icDocLoaded && (
            <button type="button" className="ic-session-notice" onClick={() => handleScanModeChange('icDocument')}>
              <span>IC Document session active: <strong>{icDocInfo?.docNo}</strong></span>
              <span className="notice-action">Return to it <ArrowRight size={16} aria-hidden="true" /></span>
            </button>
          )}

          {/* IC Document Input (only in icDocument mode) */}
          {scanMode === 'icDocument' && !icDocLoaded && (
            <div className="ic-doc-input-section">
              <h3>Load IC Document from Priority</h3>
              <label className="ic-doc-hint" htmlFor={icDocInputId}>
                IC document number
              </label>
              <div className="ic-doc-input-row">
                <input
                  id={icDocInputId}
                  type="text"
                  className="ic-doc-input"
                  placeholder="e.g. IC26000022"
                  value={icDocNo}
                  onChange={(e) => setIcDocNo(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && loadIcDocument()}
                  disabled={isLoadingIcDoc}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="ic-doc-load-btn"
                  onClick={loadIcDocument}
                  disabled={isLoadingIcDoc || !icDocNo.trim()}
                >
                  {isLoadingIcDoc ? 'Loading...' : <><FileText size={16} aria-hidden="true" /> Load Document</>}
                </button>
              </div>
            </div>
          )}

          {/* IC Document Info (when loaded) */}
          {scanMode === 'icDocument' && icDocLoaded && icDocInfo && (
            <div className="ic-doc-info">
              <div className="ic-doc-info-header">
                <div className="ic-doc-details">
                  <h3>Document: {icDocInfo.docNo}</h3>
                  <p>Warehouse: {icDocInfo.warehouse || 'N/A'} | Location: {icDocInfo.location || 'N/A'}</p>
                </div>
                <div className="st-save-status" role="status" aria-live="polite">
                  {renderSaveChip()}
                </div>
              </div>
            </div>
          )}

          {/* Barcode Scanner + result of the last scan */}
          <div className="stock-take-scanner st-scan-area">
            <BarcodeScanner
              onScan={handleScan}
              active={scanActive}
              setActive={setScanActive}
            />
            <LastScanPanel
              status={lastScan?.status || 'idle'}
              title={lastScan?.title}
              detail={lastScan?.detail}
              meta={lastScan ? lastScan.meta : (scanMode === 'icDocument' && !icDocLoaded ? 'Load an IC document to start' : 'Scan a barcode to count it')}
              timestamp={lastScan?.at}
              compact={isPhone}
              className="st-last-scan"
            />
          </div>

          {/* Summary - free scan (IC progress is in the bottom bar and the table) */}
          {scanMode === 'free' ? (
            <div className="stock-take-summary">
              <span className="summary-label">Scanned Items:</span>
              <span className="summary-value">
                {uniqueItems} unique barcode{uniqueItems !== 1 ? 's' : ''}, {totalCount} total item{totalCount !== 1 ? 's' : ''}
              </span>
            </div>
          ) : icDocLoaded && (
            <div className="stock-take-summary ic-doc-summary">
              <div className="ic-summary-heading">
                <span className="ic-summary-counted">
                  <strong>{icCountedLines}</strong> of {icDocItems.length} line{icDocItems.length !== 1 ? 's' : ''} counted
                </span>
                <span className="ic-summary-breakdown">
                  {icStatusCounts.not_scanned > 0 && <span className="ic-summary-missing">{icStatusCounts.not_scanned} missing</span>}
                  {icStatusCounts.partial > 0 && <span className="ic-summary-short">{icStatusCounts.partial} short</span>}
                  {icStatusCounts.over > 0 && <span className="ic-summary-over">{icStatusCounts.over} over</span>}
                  {icStatusCounts.not_scanned + icStatusCounts.partial + icStatusCounts.over === 0 && icDocItems.length > 0 && (
                    <span className="ic-summary-done">Everything matches</span>
                  )}
                </span>
              </div>
              <Meter value={icCountedLines} max={icDocItems.length} size="lg" label={`${icCountedLines} of ${icDocItems.length} lines counted`} />
            </div>
          )}

          {/* IC Document Items Table (when in IC mode and loaded) */}
          {scanMode === 'icDocument' && icDocLoaded && (
            <ICDocumentTable items={icItemsWithStatus} onEditLine={openIcLineEdit} highlight={highlight} />
          )}

          {/* Free Scan Items Table (when in free mode) */}
          {scanMode === 'free' && (
            <FreeScanTable
              itemsArray={itemsArray}
              isCompact={isCompact}
              incrementCount={incrementCount}
              decrementCount={decrementCount}
              removeItem={removeItem}
              onEditCount={openFreeLineEdit}
              formatTime={formatTime}
            />
          )}

          {/* Empty state - different for each mode */}
          {scanMode === 'free' && itemsArray.length === 0 && (
            <div className="stock-take-empty">
              <p>No items scanned yet. Start scanning barcodes to add items.</p>
            </div>
          )}

          {scanMode === 'icDocument' && !icDocLoaded && (
            <div className="stock-take-empty">
              <p>Enter an IC document number above to load items for verification.</p>
            </div>
          )}

          {/* Destructive actions, kept away from the main controls */}
          {(scanMode === 'free' ? freeScannedItems.size > 0 : icDocLoaded) && (
            <div className="st-danger-zone">
              {scanMode === 'free' ? (
                <button type="button" className="st-btn st-btn-danger-ghost" onClick={() => setDangerAction('clearFree')}>
                  <Trash2 size={18} aria-hidden="true" /> Clear all items
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="st-btn st-btn-danger-ghost"
                    onClick={() => setDangerAction('clearCounts')}
                    disabled={icScannedItems.size === 0}
                  >
                    <Trash2 size={18} aria-hidden="true" /> Reset counts
                  </button>
                  <button type="button" className="st-btn st-btn-danger-ghost" onClick={() => setDangerAction('closeDocument')}>
                    <RotateCcw size={18} aria-hidden="true" /> Close document
                  </button>
                </>
              )}
            </div>
          )}

          {/* Sticky bottom bar: progress + Submit */}
          {showBottomBar && (
            <div className="stock-take-footer st-bottom-bar">
              <div className="st-bottom-progress">
                {scanMode === 'free' ? (
                  <span><strong>{uniqueItems}</strong> barcode{uniqueItems !== 1 ? 's' : ''} &middot; <strong>{totalCount}</strong> item{totalCount !== 1 ? 's' : ''}</span>
                ) : (
                  <>
                    <span><strong>{icCountedLines}</strong> / {icDocItems.length} lines counted</span>
                    <Meter value={icCountedLines} max={icDocItems.length} />
                  </>
                )}
              </div>
              <button
                type="button"
                className={`submit-stock-take-button ${scanMode === 'icDocument' ? 'ic-submit' : ''}`.trim()}
                onClick={handleSubmitClick}
                disabled={isSubmitting || (scanMode === 'free' ? freeScannedItems.size === 0 : !icDocLoaded)}
              >
                {isSubmitting ? 'Submitting...' : <><Send size={18} aria-hidden="true" /> Review &amp; Submit</>}
              </button>
            </div>
          )}
        </>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <StockTakeHistory
          historyData={historyData}
          isLoadingHistory={isLoadingHistory}
          historyError={historyError}
          selectedDocument={selectedDocument}
          setSelectedDocument={setSelectedDocument}
          historySearchTerm={historySearchTerm}
          setHistorySearchTerm={setHistorySearchTerm}
          loadHistory={loadHistory}
        />
      )}

      {/* Review before submit */}
      <ReviewSubmitDialog
        open={showConfirmModal}
        scanMode={scanMode}
        icDocInfo={icDocInfo}
        icLines={icItemsWithStatus}
        freeItems={scanMode === 'free' ? itemsArray : []}
        isSubmitting={isSubmitting}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubmit}
      />

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay" role="alert" aria-busy="true">
          <div className="loading-content">
            <div className="loading-spinner" aria-hidden="true"></div>
            <p>Submitting to Priority ERP...</p>
            <p className="loading-subtext">Please wait. Scanning is paused.</p>
          </div>
        </div>
      )}

      <SuccessModal
        show={showSuccessModal}
        submissionResult={submissionResult}
        onClose={handleCloseSuccessModal}
      />

      <ErrorModal
        show={showErrorModal}
        errorDetails={errorDetails}
        onClose={handleCloseErrorModal}
      />

      <DangerConfirmDialog
        action={dangerAction ? DANGER_ACTIONS[dangerAction] : null}
        onCancel={() => setDangerAction(null)}
        onConfirm={confirmDangerAction}
      />

      <ResumeSessionDialog
        prompt={resumePrompt}
        onResume={() => resolveResumePrompt(true)}
        onStartNew={() => resolveResumePrompt(false)}
      />

      {editLine && (
        <LineEditDialog
          key={`${editLine.mode}-${editLine.barcode}`}
          line={editLine}
          onSave={saveLineEdit}
          onClose={() => setEditLine(null)}
        />
      )}

      {/* Quantity Popup for IC Document Mode */}
      <QuantityPopup
        show={showQuantityPopup}
        popupItem={popupItem}
        quantityText={popupQuantityText}
        setQuantityText={setPopupQuantityText}
        onConfirm={handleQuantityConfirm}
        onCancel={handleQuantityCancel}
      />

      <UndoBar undo={undo} onUndo={undoRemove} onDismiss={() => setUndo(null)} />
    </div>
  );
};

export default StockTake;
